/**
 * «رخدادهای خودم» — همون‌هایی که بالای صفحه‌ی کشف پین می‌شن.
 *
 * قاعده‌ی کاربر یه جمله بود: *رخدادی که کسی اضافه می‌کنه، تا وقتی تاریخِ
 * پایانش نرسیده بالای صفحه‌ی رخدادهاش پین بمونه*. سه تا نکته‌ی ریز توی همون
 * یه جمله هست که این فایل درباره‌ی اون‌هاست:
 *
 *   • **بلافاصله**، نه بعد از تایید و نه بعد از تاریخِ شروع. یعنی چیزی که
 *     هنوز توی صفِ بازبینیه هم پین می‌شه — ولی با برچسبی که می‌گه هنوز
 *     تایید نشده.
 *   • **تا تاریخِ پایان**، نه تا تاریخِ شروع. رخدادِ سه‌روزه روزِ دوم هم
 *     پینه.
 *   • **فقط برای خودش.** این فهرست هیچ‌وقت به کسِ دیگه‌ای نشون داده نمی‌شه؛
 *     چیزی که بقیه می‌بینن همون فهرستِ عادیه، بعد از تایید.
 *
 * چرا این‌جا و نه توی صفحه: هر دو اپ (گوشی و سایت) دقیقاً همین فهرست و همین
 * برچسب‌ها رو نشون می‌دن. یه ردیفِ رخداد می‌تونه چند تا **نسخه** داشته باشه
 * (مایگریشن 0033) و انتخابِ اینکه کدوم نسخه نماینده‌ی این رخداده منطقیه که
 * دو بار نوشتنش یعنی دو تا رفتارِ کمی متفاوت.
 */
import type { EventItem } from '../domain/types.ts';

/**
 * «توی توضیحات اینا رو بنویس» — فهرستی که زیرِ کادرِ توضیحاتِ رخداد نشون
 * داده می‌شه، به همون ترتیبی که کسی که می‌خواد بیاد سوال می‌پرسه: اول
 * «این چیه»، آخر چیزهای عملی.
 *
 * این‌جاست نه توی خودِ فرم، چون سه تا فرم لازمش دارن (پیشنهادِ گوشی،
 * پیشنهادِ سایت، ویرایشِ رخدادِ خودم) و یه فهرستِ کپی‌شده بعد از اولین
 * تغییر می‌شه سه تا فهرستِ کمی متفاوت. مقدارها کلیدِ i18nـن.
 */
export const DESCRIBE_KEYS = [
  'suggestDescribeWhat',
  'suggestDescribeType',
  'suggestDescribeHost',
  'suggestDescribeAges',
  'suggestDescribeParking',
  'suggestDescribeAccess',
  'suggestDescribeBring',
] as const;

/** سه شمارشِ یکتا که `my_event_stats` برمی‌گردونه (مایگریشن 0077). */
export interface MyEventStats {
  /** چند نفر کارتش رو توی فهرست دیدن. */
  impressions: number;
  /** چند نفر صفحه‌ش رو باز کردن. */
  detailViews: number;
  /** چند نفر روی لینک/بلیط کلیک کردن. */
  linkClicks: number;
}

export const EMPTY_STATS: MyEventStats = { impressions: 0, detailViews: 0, linkClicks: 0 };

/**
 * وضعیتی که روی کارتِ پین‌شده نوشته می‌شه.
 *
 *   pending      هنوز توی صفِ بازبینی — بقیه نمی‌بیننش.
 *   live         منتشر شده و توی فهرستِ همه هست.
 *   editPending  خودش منتشره، ولی یه ویرایشِ تازه منتظرِ تاییده.
 *   rejected     ادمین ردش کرده؛ دلیلش توی reviewNote است.
 */
export type MyEventState = 'pending' | 'live' | 'editPending' | 'rejected';

export interface MyEvent {
  /** ریشه‌ی زنجیره‌ی نسخه‌ها — کلیدِ پایدارِ این رخداد، حتی بعد از ویرایش. */
  rootId: string;
  /** نسخه‌ای که نماینده‌ی این رخداده: همونی که باز و ویرایش می‌شه. */
  item: EventItem;
  state: MyEventState;
  /** دلیلِ ردِ ادمین. فقط وقتی state === 'rejected' معنی داره. */
  reviewNote?: string | null;
}

/** ریشه‌ی زنجیره. خودِ ریشه بودن یعنی `rootId` همون `id` است. */
const rootOf = (e: EventItem): string => e.rootId ?? e.id;

/**
 * ترتیبِ «کدوم نسخه نماینده‌ست»: منتشرشده، بعد توی صف، بعد ردشده.
 *
 * عددِ بزرگ‌تر یعنی مهم‌تر. هر وضعیتِ دیگه‌ای صفر می‌گیره و پایین‌تر فیلتر
 * می‌شه — `superseded` و `merged`، و از مایگریشن 0078 `withdrawn` هم: چیزی
 * که خودِ کاربر پس گرفته نباید توی فهرستِ خودش بمونه.
 */
const rank = (e: EventItem): number => {
  if (e.status === 'published') return 3;
  if (e.status === 'pending_review') return 2;
  if (e.status === 'rejected') return 1;
  return 0;
};

/**
 * هنوز تموم نشده؟
 *
 * فقط `endsAt` مهمه، و برای رخدادِ کاربر از مایگریشن 0077 اجباریه. ردیفِ
 * بی‌پایان (پیشنهادهای قبل از اون مایگریشن) عمداً پین **نمی‌شه**: چیزی که
 * پایان نداره یعنی پینی که هیچ‌وقت برداشته نمی‌شه.
 */
const stillOn = (e: EventItem, now: Date): boolean =>
  !!e.endsAt && new Date(e.endsAt).getTime() > now.getTime();

/**
 * ردیف‌های خامِ «رخدادهای من» → فهرستِ پین، یکی به‌ازای هر رخداد.
 *
 * ورودی همه‌ی نسخه‌هاییه که کاربر فرستاده (نگاه کن به eventRepo.mine)؛
 * خروجی یکی برای هر ریشه، مرتب بر اساسِ نزدیک‌ترین پایان — چیزی که زودتر
 * تموم می‌شه بالاتره، چون فرصتِ کاری کردن براش کمتره.
 */
export function pinnedEvents(rows: EventItem[], now: Date = new Date()): MyEvent[] {
  const byRoot = new Map<string, EventItem[]>();
  for (const row of rows) {
    if (rank(row) === 0) continue;
    const key = rootOf(row);
    const list = byRoot.get(key);
    if (list) list.push(row); else byRoot.set(key, [row]);
  }

  const out: MyEvent[] = [];
  for (const [rootId, versions] of byRoot) {
    // نماینده: بالاترین رتبه، و بینِ هم‌رتبه‌ها تازه‌ترین نسخه.
    const item = [...versions].sort((a, b) => (
      rank(b) - rank(a) || (b.version ?? 0) - (a.version ?? 0)
    ))[0];
    if (!stillOn(item, now)) continue;

    const hasPending = versions.some((v) => v.status === 'pending_review' && v.id !== item.id);
    const state: MyEventState = item.status === 'published'
      ? (hasPending ? 'editPending' : 'live')
      : item.status === 'rejected' ? 'rejected' : 'pending';

    out.push({ rootId, item, state, reviewNote: item.reviewNote ?? null });
  }

  return out.sort((a, b) => (
    new Date(a.item.endsAt as string).getTime() - new Date(b.item.endsAt as string).getTime()
  ));
}

/**
 * دو نسبتی که کاربر خواست، هر دو با هم.
 *
 *   openRate   چند درصدِ کسانی که کارت رو دیدن، بازش کردن.
 *   clickRate  چند درصدِ کسانی که بازش کردن، روی لینک/بلیط زدن.
 *
 * `null` یعنی مخرج صفره — یعنی «هنوز معلوم نیست»، نه «صفر درصد». دو تا
 * چیزِ کاملاً متفاوتن: اولی یعنی هیچ‌کس هنوز ندیده، دومی یعنی دیدن و رد شدن.
 */
export const openRate = (s: MyEventStats): number | null =>
  (s.impressions > 0 ? s.detailViews / s.impressions : null);

export const clickRate = (s: MyEventStats): number | null =>
  (s.detailViews > 0 ? s.linkClicks / s.detailViews : null);

/** نسبت → درصدِ خوانا. `null` می‌شه «—»، نه «۰٪». */
export const percentLabel = (rate: number | null): string =>
  (rate === null ? '—' : `${Math.round(rate * 100)}%`);
